import {Component, computed, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {MatFormField, MatHint, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatGridList, MatGridTile} from "@angular/material/grid-list";
import {MatIcon} from "@angular/material/icon";
import {MatInput} from "@angular/material/input";
import {MatOption} from "@angular/material/core";
import {MatSelect, MatSelectTrigger} from "@angular/material/select";
import {ReactiveFormsModule} from "@angular/forms";
import {SharedModule} from "../../../shared/shared.module";
import {FinanceFilterModalService} from "../../service/finance-filter-modal.service";
import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";
import {TravelTypeModel} from "../../../general-ledger/model/general-ledger";
import {SubmitToResponse} from "../../../shared/model/pay";
import {TramsDataSourceModel} from "../../../payments/models/trams-data-source";
import {BranchModel, BranchResponse} from "../../../shared/model/bank";
import {FinanceFilterModel} from "../../models/finance-filter-model";
import {DEFAULT_DATA_SOURCE_ID} from "../../constants/finance-filter-constants";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {firstValueFrom} from "rxjs";
import moment from 'moment';

interface DialogData {
  data: [TravelTypeModel[], SubmitToResponse, TramsDataSourceModel[], BranchResponse];
}

@Component({
    selector: 'app-finance-filter-modal',
    imports: [
        MatButton,
        MatDatepicker,
        MatDatepickerInput,
        MatDatepickerToggle,
        MatFormField,
        MatGridList,
        MatGridTile,
        MatHint,
        MatIcon,
        MatIconButton,
        MatInput,
        MatLabel,
        MatOption,
        MatSelect,
        MatSelectTrigger,
        MatSuffix,
        ReactiveFormsModule,
        SharedModule,
    ],
    templateUrl: './finance-filter-modal.component.html'
})
export class FinanceFilterModalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  private readonly dialogRef = inject(MatDialogRef<FinanceFilterModalComponent>);

  private readonly data = inject<DialogData>(MAT_DIALOG_DATA);

  private readonly financeFilterModalService = inject(FinanceFilterModalService);

  private readonly breakpointObserver = inject(BreakpointObserver);

  protected readonly form;

  protected readonly travelTypes = computed(() => this.data.data[0]);

  protected readonly allTravelTypes = computed(() => this.travelTypes().map(t => t.id));

  protected readonly submitToList = computed(() => this.data.data[1].items);

  protected readonly allSubmitToList = computed(() => this.submitToList().map(s => s.submitToLinkCode));

  protected readonly tramsDataSource = computed(() => this.data.data[2]);

  protected readonly branches = signal<BranchModel[]>([]);

  protected readonly allBranches = computed(() => this.branches().map(b => b.id));

  protected isSmall: boolean = false;

  protected get showIssueDateFromClearIcon(): boolean {
    const currentValue = this.form.controls.IssueDateFrom.value;
    const defaultValue = this.form.controls.IssueDateFrom.defaultValue;

    if (!currentValue && !defaultValue) return false;
    if (!currentValue || !defaultValue) return true;

    return !moment(currentValue).isSame(defaultValue, 'day');
  }

  protected get showIssueDateToClearIcon(): boolean {
    const currentValue = this.form.controls.IssueDateTo.value;
    const defaultValue = this.form.controls.IssueDateTo.defaultValue;

    if (!currentValue && !defaultValue) return false;
    if (!currentValue || !defaultValue) return true;

    return !moment(currentValue).isSame(defaultValue, 'day');
  }

  protected get showTramsDataSourceClearIcon(): boolean {
    return this.form.controls.DataSourceId.value !== null;
  }

  constructor() {
    const FILTERED_BRANCHES = this.data.data[3].items
      .filter(b => b.dataSourceId === DEFAULT_DATA_SOURCE_ID);
    const DEFAULT_BRANCHES = FILTERED_BRANCHES
      .map(b => b.id);
    this.form = this.financeFilterModalService.getForm(
      this.allTravelTypes(),
      this.allSubmitToList(),
      DEFAULT_BRANCHES
    );
    this.branches.set(FILTERED_BRANCHES);

    this.breakpointObserver.observe([
      Breakpoints.Medium,
      Breakpoints.Small,
      Breakpoints.XSmall
    ]).subscribe(result => {
      this.isSmall = result.matches;
    });
  }

  public ngOnInit() {
    this.patchForm();
    this.subscribeToControls();
  }

  private async patchForm() {
    const {IssueDateFrom, IssueDateTo, ...rest} = await firstValueFrom(this.financeFilterModalService.getFilters());
    this.form.patchValue({
      ...rest,
      IssueDateFrom: IssueDateFrom,
      IssueDateTo: IssueDateTo,
    });
  }

  private subscribeToControls() {
    this.form.controls.DataSourceId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.branches.set(this.data.data[3].items
          .filter(b => b.dataSourceId === value));
        this.form.controls.BranchIds.setValue(this.allBranches());
      });
  }

  protected getTravelTypeNameById(id?: number): string {
    const data = this.travelTypes();
    return data.find((item) => item.id === id)?.travelTypeName || '';
  }

  protected getSubmitTypeNameByCode(code?: string): string {
    const data = this.submitToList();
    return data.find((item) => item.submitToLinkCode === code)?.submitToLink || '';
  }

  protected getBranchNameById(id?: number): string {
    const data = this.branches();
    return data.find((item) => item.id === id)?.name || '';
  }

  protected resetFilter(control: keyof Omit<FinanceFilterModel, 'ReloadGrid'>) {
    this.form.controls[control].reset();
  }

  protected close() {
    this.dialogRef.close();
  }

  protected apply() {
    const form = this.form.getRawValue();
    const {IssueDateFrom, IssueDateTo, ...rest} = form;
    this.financeFilterModalService.setFilters({
      ...rest,
      ReloadGrid: true,
      IssueDateFrom: IssueDateFrom,
      IssueDateTo: IssueDateTo
    });
    this.close();
  }

}
